/**
 * The frame every signed-in screen is drawn inside.
 *
 * One function rather than a header per screen, because the things it carries —
 * which nav item is current, who is signed in, the language switch — are
 * exactly the things a screen would forget.
 *
 * The test-mode banner is gone with test mode (PLAN §17). Nothing
 * replaces it, deliberately: a banner that says "this is real" on every screen
 * of a panel that is always real is a banner nobody reads.
 */

import { el, replace, type Child } from './dom.ts';
import { healthStrip } from './screens/health.ts';
import { chooseLocale, locale, translate as t, LOCALES } from './i18n.ts';
import { signOut } from './session.ts';

export type NavKey = 'campaign.new' | 'campaigns';

export interface Shell {
  readonly active?: NavKey;
  readonly email: string;
  readonly title: string;
}

export function renderShell(root: Element, shell: Shell, ...body: Child[]): void {
  replace(
    root,
    el(
      'header',
      { class: 'masthead' },
      el('a', { class: 'brand', href: '/' }, t('app.name')),
      el(
        'nav',
        { class: 'nav' },
        navLink('campaign.new', '/campaign/new', t('nav.newCampaign'), shell.active),
        navLink('campaigns', '/campaigns', t('nav.history'), shell.active),
      ),
      el(
        'div',
        { class: 'account' },
        el('span', { class: 'username' }, shell.email),
        localeSwitcher(),
        el(
          'button',
          {
            type: 'button',
            class: 'link-button',
            onclick: () => {
              void signOut().then(() => navigate('/'));
            },
          },
          t('nav.logout'),
        ),
      ),
    ),
    // Between the masthead and the page, on every signed-in screen. C12 is the
    // argument: for eight days every screen agreed that campaigns were sending
    // while nothing was, and no surface anywhere could say otherwise.
    healthStrip(),
    el('main', { class: 'page' }, ...body),
  );

  document.title = `${shell.title} — ${t('app.name')}`;
}

function navLink(key: NavKey, href: string, label: string, active?: NavKey) {
  // `aria-current` rather than a class alone: the current page has to be
  // announced, not only shaded.
  return el(
    'a',
    {
      class: key === active ? 'nav-item is-current' : 'nav-item',
      href,
      'aria-current': key === active ? 'page' : undefined,
      onclick: link(href),
    },
    label,
  );
}

function localeSwitcher() {
  const current = locale();
  return el(
    'div',
    { class: 'locale-switcher' },
    ...LOCALES.filter((one) => one !== current).map((one) =>
      el(
        'button',
        {
          type: 'button',
          class: 'link-button',
          lang: one,
          onclick: () => {
            chooseLocale(one);
            // A reload rather than a re-render: the catalogue is fetched once
            // at boot, and every screen already on the page holds sentences
            // from the old one.
            window.location.reload();
          },
        },
        one.toUpperCase(),
      ),
    ),
  );
}

/**
 * Intercepts a link so the panel navigates without a round trip, while the
 * `href` stays a real address — so middle-click, "open in new tab" and copying
 * the link all still work, and Cloudflare's `/* /index.html 200` serves it.
 */
export function link(href: string): (event: Event) => void {
  return (event) => {
    const mouse = event as MouseEvent;
    if (mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.button !== 0) return;

    event.preventDefault();
    navigate(href);
  };
}

export function navigate(href: string): void {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** A screen that is still fetching. Said out loud rather than left blank. */
export function loading(): Child {
  return el('p', { class: 'muted', role: 'status' }, t('app.loading'));
}

/**
 * Something did not load.
 *
 * A retry button rather than only a message, because the commonest cause is a
 * connection that has come back by the time anybody reads the sentence.
 */
export function failure(retry: () => void): Child {
  return el(
    'div',
    { class: 'finding error' },
    el('span', {}, t('app.loadFailed')),
    el('button', { type: 'button', class: 'secondary', onclick: retry }, t('app.retry')),
  );
}
